import { useConnection } from "@solana/wallet-adapter-react";
import type { PublicKey } from "@solana/web3.js";
import type { QuarryPositionsResponse } from "@stepfinance/farms";
import { QuarryFarmPositionsFetcher } from "@stepfinance/farms";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { useLiquidity } from "../../../contexts/liquidity";
import { useMarkets } from "../../../contexts/market";
import { cache } from "../../../utils/cache";
import { getTokenByMint } from "../../../utils/tokenInfo";
import { useConnectedWallet } from "../../wallet";
import { useLiquidityPoolsQuery } from "../liquidity/useLiquidityPoolsQuery";

const DEFAULT_QUARRY_POSITIONS: QuarryPositionsResponse = {
  farmPositions: [],
  stakingPositions: [],
};

export const useQuarryPositionsQuery = () => {
  const { connection } = useConnection();
  const wallet = useConnectedWallet();
  const { priceInUSD, marketsInitialised } = useMarkets();
  const { saberPools, sarosPools, marinadePools, atrixPools } = useLiquidity();
  const { isSuccess } = useLiquidityPoolsQuery();

  const fetcher = useMemo(() => {
    if (wallet && isSuccess) {
      const underlyingPoolsData = { ...marinadePools, ...saberPools, ...sarosPools, ...atrixPools };
      return new QuarryFarmPositionsFetcher({
        connection,
        wallet,
        underlyingPoolsData,
        getMint: (pubKey: string | PublicKey | undefined) => cache.getMint(pubKey) as unknown as any, // TODO remove when spl-token upgrade pr merged
        getTokenByMint,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connection, isSuccess, wallet]);

  return useQuery<QuarryPositionsResponse>(
    ["quarryPositions", fetcher?.wallet.publicKey.toBase58()],
    async () => {
      if (fetcher) {
        return await fetcher.fetchPositions(priceInUSD);
      }
      return DEFAULT_QUARRY_POSITIONS;
    },
    {
      retry: false,
      enabled: !!fetcher && marketsInitialised && !!priceInUSD && isSuccess,
    },
  );
};
